/* Videos the site embeds, with the facts Google's video markup needs. Every
   value here is read off the video itself on YouTube (title, upload date,
   length, chapter times), so the markup describes the real video. The
   description is ours and plain; the YouTube one is written for YouTube. */

export type SiteVideo = {
  id: string;
  title: string;
  description: string;
  /** ISO 8601, as YouTube reports it. */
  uploadDate: string;
  /** ISO 8601 duration. */
  duration: string;
  seconds: number;
  channel: string;
  channelUrl: string;
  /** The video's own chapters, in seconds from the start. */
  chapters: { at: number; label: string }[];
};

export const DEADLOCK_SHOWCASE: SiteVideo = {
  id: "fqI1cyAaDGM",
  title: "Deadlock Hack | Cheat | Veyra | External | Free Version at Discord",
  description:
    "An eleven-minute walkthrough of the Veyra Deadlock cheat menu: the aimbot, visuals and ESP, hero scripts, parry and counterspell scripts and the rest of the scripts, shown in game.",
  uploadDate: "2026-04-14T16:24:21-07:00",
  duration: "PT11M5S",
  seconds: 665,
  channel: "Veyra Cheats",
  channelUrl: "https://www.youtube.com/@VeyraCheats",
  chapters: [
    { at: 0, label: "Menu showcase" },
    { at: 28, label: "Aimbot" },
    { at: 69, label: "Visuals and ESP" },
    { at: 273, label: "Hero scripts" },
    { at: 455, label: "Parry scripts" },
    { at: 477, label: "Counterspell scripts" },
    { at: 629, label: "Other scripts" },
  ],
};

/* The COLD montage: real matches, the "bro how did you get so good" chat,
   the new heroes in the hero scripts. Values read off YouTube on 2026-10-20. */
export const DEADLOCK_MONTAGE: SiteVideo = {
  id: "DXASb9_WS10",
  title: "I Tested The BEST UNDETECTED Deadlock Cheat After The Update 😳 (Aimbot + ESP)",
  description:
    "The Veyra Deadlock cheat in real matches: aimbot, ESP wallhack and hero scripts, with the streamproof overlay and the new heroes.",
  uploadDate: "2026-10-19T13:57:25-07:00",
  duration: "PT1M33S",
  seconds: 93,
  channel: "Veyra Cheats",
  channelUrl: "https://www.youtube.com/@VeyraCheats",
  chapters: [
    { at: 0, label: "\"bro how did you get so good\"" },
    { at: 20, label: "The kills" },
    { at: 60, label: "\"wait whats veyra\"" },
    { at: 74, label: "More kills" },
  ],
};

export const watchUrl = (v: SiteVideo, at = 0) =>
  `https://www.youtube.com/watch?v=${v.id}${at ? `&t=${at}s` : ""}`;
export const embedUrl = (v: SiteVideo) => `https://www.youtube.com/embed/${v.id}`;
export const thumbUrls = (v: SiteVideo) => [
  `https://i.ytimg.com/vi/${v.id}/maxresdefault.jpg`,
  `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`,
];
export const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/** VideoObject markup, with the chapters as Clips so search can show them as
    key moments. */
export function videoJsonLd(v: SiteVideo, pageUrl: string) {
  return {
    "@type": "VideoObject",
    "@id": `${pageUrl}#video`,
    name: v.title,
    description: v.description,
    thumbnailUrl: thumbUrls(v),
    uploadDate: v.uploadDate,
    duration: v.duration,
    embedUrl: embedUrl(v),
    url: watchUrl(v),
    publisher: { "@type": "Organization", name: "Veyra", url: "https://veyra.services/" },
    hasPart: v.chapters.map((c, i) => ({
      "@type": "Clip",
      name: c.label,
      startOffset: c.at,
      endOffset: v.chapters[i + 1]?.at ?? v.seconds,
      url: watchUrl(v, c.at),
    })),
  };
}
